import { RouterModule } from '@angular/router';
import { NgModule } from '@angular/core';
import { AppLayoutComponent } from './layout/app.layout.component';
import { ChatComponent } from './features/chat/chat.component';
import { BotGuard } from './core/guards/bot.guard';
import { schedulingEnabledGuard } from './core/guards/scheduling-enabled.guard';
import { SubscriptionsComponent } from './features/subscriptions/subscriptions.component';
import { WidgetConfigComponent } from './features/widget-config/widget-config.component';
import { GlobotDashboardComponent } from './features/dashboard/dashboard/dashboard.component';
import { GlobotLiveComponent } from './features/dashboard/globot-live/globot-live.component';

@NgModule({
  imports: [
    RouterModule.forRoot(
      [
        { path: '', redirectTo: 'dashboard/login', pathMatch: 'full' },
        {
          path: 'dashboard/login',
          loadComponent: () =>
            import('./features/auth/auth.component').then((c) => c.AuthComponent),
        },
        {
          path: 'dashboard/register',
          loadComponent: () =>
            import('./features/register/register.component').then((c) => c.RegisterComponent),
        },
        {
          path: 'dashboard/recover-password',
          loadComponent: () =>
            import('./features/recover-password/recover-password.component').then(
              (c) => c.RecoverPasswordComponent
            ),
        },
        {
          path: 'dashboard/user-password',
          loadComponent: () =>
            import('./features/user-password/user-password.component').then(
              (c) => c.UserPasswordComponent
            ),
        },
        {
          path: 'dashboard/workspace/invite',
          loadComponent: () =>
            import('./features/workspace-invite/workspace-invite.component').then(
              (c) => c.WorkspaceInviteComponent
            ),
        },
        {
          path: 'dashboard',
          component: AppLayoutComponent,
          children: [
            {
              path: 'action/:action',
              loadComponent: () =>
                import('./features/dashboard/bot/bot.component').then((c) => c.BotComponent),
              canActivate: [BotGuard],
            },
            {
              path: '',
              loadComponent: () =>
                import('./features/dashboard/bot/bot.component').then((c) => c.BotComponent),
              canActivate: [BotGuard],
            },
            {
              path: 'new-bot',
              loadComponent: () =>
                import('./features/dashboard/new-bot/new-bot.component').then(
                  (c) => c.NewBotComponent
                ),
            },
            {
              path: 'edit-user',
              loadComponent: () =>
                import('./features/dashboard/edit-user/edit-user.component').then(
                  (c) => c.EditUserComponent
                ),
              canActivate: [BotGuard],
            },
            {
              path: 'workspace-settings',
              loadComponent: () =>
                import('./features/dashboard/workspace-settings/workspace-settings.component').then(
                  (c) => c.WorkspaceSettingsComponent
                ),
              canActivate: [BotGuard],
            },
            {
              path: 'team',
              loadComponent: () =>
                import('./features/dashboard/team/team.component').then((c) => c.TeamComponent),
              canActivate: [BotGuard],
            },
            {
              path: 'notifications',
              loadComponent: () =>
                import('./features/dashboard/notifications/notifications.component').then(
                  (c) => c.NotificationsComponent
                ),
              canActivate: [BotGuard],
            },
            {
              path: 'crm/clientes',
              loadComponent: () =>
                import('./features/crm/crm-clientes.component').then((c) => c.CrmClientesComponent),
              canActivate: [BotGuard],
            },
            {
              path: 'crm/clientes/:clientId',
              loadComponent: () =>
                import('./features/crm/clientes/client-detail.component').then(
                  (c) => c.ClientDetailComponent
                ),
              canActivate: [BotGuard],
            },
            {
              path: 'crm/pipeline',
              loadComponent: () =>
                import('./features/crm/crm-clientes.component').then((c) => c.CrmClientesComponent),
              canActivate: [BotGuard],
              data: { view: 'tablero' },
            },
            {
              path: 'crm/dashboard',
              loadComponent: () =>
                import('./features/crm/crm-clientes.component').then((c) => c.CrmClientesComponent),
              canActivate: [BotGuard],
              data: { view: 'resumen' },
            },
            {
              path: 'edit-bot/:id',
              loadComponent: () =>
                import('./features/dashboard/edit-bot/edit-bot.component').then(
                  (c) => c.EditBotComponent
                ),
              canActivate: [BotGuard],
              children: [
                { path: '', redirectTo: 'widget-config', pathMatch: 'full' },
                { path: 'chat', component: ChatComponent },
                { path: 'widget-config', component: WidgetConfigComponent },
                { path: 'dashboard', component: GlobotDashboardComponent },
                { path: 'leads', redirectTo: '/dashboard/crm/clientes', pathMatch: 'full' },
                { path: 'clients', redirectTo: '/dashboard/crm/clientes', pathMatch: 'full' },
                { path: 'live', component: GlobotLiveComponent },
                {
                  path: 'datasource',
                  loadComponent: () =>
                    import('./features/dashboard/edit-bot/datasource/datasource.component').then(
                      (c) => c.DatasourceComponent
                    ),
                },
                {
                  path: 'embed',
                  loadComponent: () =>
                    import('./features/dashboard/edit-bot/embed/embed.component').then(
                      (c) => c.EmbedComponent
                    ),
                },
                {
                  path: 'config',
                  loadComponent: () =>
                    import('./features/dashboard/edit-bot/config/config.component').then(
                      (c) => c.ConfigComponent
                    ),
                },
                {
                  path: 'whatsapp',
                  loadComponent: () =>
                    import(
                      './features/dashboard/edit-bot/channels/whatsapp-channel/whatsapp-channel.component'
                    ).then((c) => c.WhatsappChannelComponent),
                },
                {
                  path: 'messenger',
                  loadComponent: () =>
                    import(
                      './features/dashboard/edit-bot/channels/messenger-channel/messenger-channel.component'
                    ).then((c) => c.MessengerChannelComponent),
                },
                {
                  path: 'instagram',
                  loadComponent: () =>
                    import(
                      './features/dashboard/edit-bot/channels/instagram-channel/instagram-channel.component'
                    ).then((c) => c.InstagramChannelComponent),
                },
                {
                  path: 'records',
                  loadComponent: () =>
                    import('./features/dashboard/edit-bot/records/records.component').then(
                      (c) => c.RecordsComponent
                    ),
                },
                {
                  path: 'scheduling',
                  canMatch: [schedulingEnabledGuard],
                  loadComponent: () =>
                    import('./features/dashboard/scheduling/scheduling.component').then(
                      (c) => c.SchedulingComponent
                    ),
                  children: [
                    {
                      path: '',
                      loadComponent: () =>
                        import(
                          './features/dashboard/scheduling/scheduling-dashboard/scheduling-dashboard.component'
                        ).then((c) => c.SchedulingDashboardComponent),
                    },
                    {
                      path: 'services',
                      loadComponent: () =>
                        import(
                          './features/dashboard/scheduling/services-list/services-list.component'
                        ).then((c) => c.ServicesListComponent),
                    },
                    {
                      path: 'branches',
                      loadComponent: () =>
                        import(
                          './features/dashboard/scheduling/branches-list/branches-list.component'
                        ).then((c) => c.BranchesListComponent),
                    },
                    {
                      path: 'resources',
                      loadComponent: () =>
                        import(
                          './features/dashboard/scheduling/resources-list/resources-list.component'
                        ).then((c) => c.ResourcesListComponent),
                    },
                    {
                      path: 'resources/new',
                      loadComponent: () =>
                        import(
                          './features/dashboard/scheduling/resource-form/resource-form.component'
                        ).then((c) => c.ResourceFormComponent),
                    },
                    {
                      path: 'resources/:resourceId/edit',
                      loadComponent: () =>
                        import(
                          './features/dashboard/scheduling/resource-form/resource-form.component'
                        ).then((c) => c.ResourceFormComponent),
                    },
                    {
                      path: 'appointments',
                      loadComponent: () =>
                        import(
                          './features/dashboard/scheduling/appointments/appointments.component'
                        ).then((c) => c.AppointmentsComponent),
                    },
                    {
                      path: 'setup',
                      loadComponent: () =>
                        import(
                          './features/dashboard/scheduling/setup-wizard/setup-wizard.component'
                        ).then((c) => c.SetupWizardComponent),
                    },
                    {
                      path: 'schedules',
                      loadComponent: () =>
                        import(
                          './features/dashboard/scheduling/schedules/schedules.component'
                        ).then((c) => c.SchedulesComponent),
                    },
                    {
                      path: 'config',
                      loadComponent: () =>
                        import(
                          './features/dashboard/scheduling/config/scheduling-config.component'
                        ).then((c) => c.SchedulingConfigComponent),
                    },
                  ],
                },
              ],
            },
          ],
        },
        { path: 'chat/:token', component: ChatComponent },
        {
          path: 'subscription',
          component: SubscriptionsComponent,
          canActivate: [BotGuard],
        },
        {
          path: 'subscription/login',
          loadComponent: () =>
            import('./features/auth/auth.component').then((c) => c.AuthComponent),
        },
        { path: '**', redirectTo: '/dashboard' },
      ],
      {
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
        onSameUrlNavigation: 'reload',
      }
    ),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
